import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalApi } from '@/services/portal.api';

const PORTAL_KEY = ['portal'] as const;

export const portalKeys = {
  assessment:        (id: string) => [...PORTAL_KEY, 'assessments', id]                       as const,
  responses:         (id: string) => [...PORTAL_KEY, 'assessments', id, 'responses']          as const,
  evidence:          (id: string, reqId: string) => [...PORTAL_KEY, 'assessments', id, 'evidence', reqId] as const,
  history:           (id: string) => [...PORTAL_KEY, 'assessments', id, 'history']            as const,
  awaiting:          (id: string) => [...PORTAL_KEY, 'assessments', id, 'awaiting']           as const,
};

export function usePortalDashboard() {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'dashboard'],
    queryFn: () => portalApi.getDashboard(),
  });
}

export function usePortalEngagements(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'engagements', params],
    queryFn: () => portalApi.listEngagements(params),
  });
}

export function usePortalEngagement(id: string | undefined) {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'engagements', id],
    queryFn: () => portalApi.getEngagement(id!),
    enabled: !!id,
  });
}

export function usePortalEngagementReport(id: string | undefined) {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'engagements', id, 'report'],
    queryFn: () => portalApi.getEngagementReport(id!),
    enabled: !!id,
  });
}

export function usePortalRequirements(engagementId: string | undefined) {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'requirements', engagementId],
    queryFn: () => portalApi.getRequirements(engagementId!),
    enabled: !!engagementId,
  });
}

export function usePortalEvidence(engagementId: string | undefined) {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'evidence', engagementId],
    queryFn: () => portalApi.getEvidence(engagementId!),
    enabled: !!engagementId,
  });
}

export function usePortalExercises() {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'exercises'],
    queryFn: () => portalApi.listExercises(),
  });
}

export function usePortalExercise(id: string | undefined) {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'exercises', id],
    queryFn: () => portalApi.getExercise(id!),
    enabled: !!id,
  });
}

export function usePortalNotifications(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...PORTAL_KEY, 'notifications', params],
    queryFn: () => portalApi.listNotifications(params),
  });
}

// ── Assessments ────────────────────────────────────────────────────────────

export function usePortalAssessment(id: string | undefined) {
  return useQuery({
    queryKey: id ? portalKeys.assessment(id) : ['portal','assessments','noop'],
    queryFn:  () => portalApi.getAssessment(id!),
    enabled:  !!id,
    // The release lifecycle (read-only lock) can change server-side at any time
    // (e.g. auditor reopens the assessment). Always refetch on mount so the
    // client never stays locked on a stale cached status.
    staleTime:     0,
    refetchOnMount: 'always',
  });
}

export function usePortalAssessmentResponses(id: string | undefined) {
  return useQuery({
    queryKey: id ? portalKeys.responses(id) : ['portal','assessments','responses','noop'],
    queryFn:  () => portalApi.getAssessmentResponses(id!),
    enabled:  !!id,
    staleTime:     0,
    refetchOnMount: 'always',
  });
}

export function useSubmitPortalResponse(assessmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requirementId, body }: {
      requirementId: string;
      body: { status: string; maturityLevel?: string; notes?: string; evidence?: string };
    }) => portalApi.submitAssessmentResponse(assessmentId, requirementId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: portalKeys.responses(assessmentId) });
      qc.invalidateQueries({ queryKey: portalKeys.assessment(assessmentId) });
    },
  });
}

export function usePortalAssessmentEvidence(assessmentId: string, requirementId: string, enabled = true) {
  return useQuery({
    queryKey: portalKeys.evidence(assessmentId, requirementId),
    queryFn:  () => portalApi.listAssessmentEvidence(assessmentId, requirementId),
    enabled:  !!assessmentId && !!requirementId && enabled,
  });
}

export function useUploadPortalEvidence(assessmentId: string, requirementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => portalApi.uploadAssessmentEvidence(assessmentId, requirementId, file),
    onSuccess:  () => qc.invalidateQueries({ queryKey: portalKeys.evidence(assessmentId, requirementId) }),
  });
}

export function useDeletePortalEvidence(assessmentId: string, requirementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) => portalApi.deleteAssessmentEvidence(assessmentId, requirementId, docId),
    onSuccess:  () => qc.invalidateQueries({ queryKey: portalKeys.evidence(assessmentId, requirementId) }),
  });
}

/**
 * The assessment's lifecycle history. Carries the assessor's reason for
 * sending it back, which is the whole point of showing it.
 */
export function usePortalAssessmentHistory(id: string | undefined) {
  return useQuery({
    queryKey: id ? portalKeys.history(id) : ['portal', 'assessments', 'history', 'noop'],
    queryFn: () => portalApi.getAssessmentHistory(id!),
    enabled: !!id,
  });
}

/** Requirements the assessor is waiting on, with their note on each. */
export function usePortalAwaitingRequirements(id: string | undefined) {
  return useQuery({
    queryKey: id ? portalKeys.awaiting(id) : ['portal', 'assessments', 'awaiting', 'noop'],
    queryFn: () => portalApi.getAwaitingRequirements(id!),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
